import { CommonModule } from '@angular/common';
import { Component, Inject, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';

import {
  BoardItem,
  compareBoardItems,
  personalEventItem,
} from '@app/planner-board/board-item';
import { PersonalEventApi } from '@app/services/personal-event-api.service';
import { ToastService } from '@app/services/toast.service';
import {
  NewPersonalEventDialogComponent,
  NewPersonalEventDialogData,
} from '@app/new-personal-event-dialog/new-personal-event-dialog.component';

export interface PlannerDayDetailsData {
  date: Date;
  items: BoardItem[];
  organizationId: number;
}

/** Esito restituito al chiamante: 'changed' impone un ricaricamento. */
export type PlannerDayDetailsResult = 'changed' | undefined;

/**
 * Dettaglio completo di una giornata della bacheca.
 *
 * Esiste perché la cella del calendario mostra al massimo tre voci: qui
 * compare tutto quello che cade in quel giorno, con le azioni possibili
 * (aprire la fattura, modificare o eliminare un'attività).
 */
@Component({
  selector: 'izy-planner-day-details',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './planner-day-details.component.html',
  styleUrl: './planner-day-details.component.scss',
})
export class PlannerDayDetailsComponent {
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private personalEventApi = inject(PersonalEventApi);
  private toast = inject(ToastService);

  readonly items = signal<BoardItem[]>([]);
  /** Tenuto separato dal dismiss perché l'utente può fare più modifiche di fila. */
  private changed = false;

  constructor(
    private bottomSheetRef: MatBottomSheetRef<
      PlannerDayDetailsComponent,
      PlannerDayDetailsResult
    >,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: PlannerDayDetailsData,
  ) {
    this.items.set([...data.items].sort(compareBoardItems));
  }

  close(): void {
    this.bottomSheetRef.dismiss(this.changed ? 'changed' : undefined);
  }

  /** Aprire la fattura porta via dalla bacheca: il foglio va chiuso prima. */
  openInvoice(item: BoardItem): void {
    if (!item.invoice) {
      return;
    }
    const invoiceId = item.invoice.id;
    this.bottomSheetRef.dismiss(this.changed ? 'changed' : undefined);
    this.router.navigate([
      `/${this.data.organizationId}/invoice/${invoiceId}`,
    ]);
  }

  addActivity(): void {
    this.openActivityDialog({
      organizationId: this.data.organizationId,
      date: this.data.date,
    });
  }

  editActivity(item: BoardItem): void {
    if (!item.personalEvent) {
      return;
    }
    this.openActivityDialog({
      organizationId: this.data.organizationId,
      event: item.personalEvent,
    });
  }

  private openActivityDialog(data: NewPersonalEventDialogData): void {
    const ref = this.dialog.open(NewPersonalEventDialogComponent, {
      width: '90%',
      maxWidth: '450px',
      autoFocus: false,
      panelClass: 'izy-dialog-panel',
      data,
    });

    ref.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.changed = true;
        // Il foglio non può ricostruire da solo la giornata: chiude e lascia
        // che sia la bacheca a ricaricare i dati aggiornati.
        this.bottomSheetRef.dismiss('changed');
      }
    });
  }

  /**
   * Spunta o despunta un'attività.
   *
   * L'aggiornamento è ottimistico solo dopo la risposta: la riga viene
   * ricostruita dal view-model così icona, barratura e ordinamento restano
   * coerenti con quanto mostra la griglia.
   */
  toggleDone(item: BoardItem): void {
    const event = item.personalEvent;
    if (!event) {
      return;
    }
    const nextDone = !event.is_done;

    this.personalEventApi
      .setPersonalEventDone(this.data.organizationId, event.id, nextDone)
      .subscribe({
        next: () => {
          this.changed = true;
          const updated = personalEventItem({ ...event, is_done: nextDone });
          this.items.update((current) =>
            current
              .map((i) => (i.key === item.key && updated ? updated : i))
              .sort(compareBoardItems),
          );
        },
        error: (err: unknown) => {
          console.error('Errore aggiornamento attività:', err);
          this.toast.error("Non è stato possibile aggiornare l'attività");
        },
      });
  }

  deleteActivity(item: BoardItem): void {
    const event = item.personalEvent;
    if (!event) {
      return;
    }

    this.personalEventApi
      .softDeletePersonalEvent(this.data.organizationId, event.id)
      .subscribe({
        next: () => {
          this.changed = true;
          this.items.update((current) =>
            current.filter((i) => i.key !== item.key),
          );
          this.toast.success('Attività eliminata');
        },
        error: (err: unknown) => {
          console.error('Errore eliminazione attività:', err);
          this.toast.error("Non è stato possibile eliminare l'attività");
        },
      });
  }
}
